import { useEffect, useRef, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { useCart, PRODUCT, inr } from "@/context/CartContext";
import { useAuth } from "@/context/AuthContext";
import { track } from "@/utils/ga";

const FIELDS = [
  { name: "name", label: "FULL NAME", type: "text", testid: "checkout-name", span: true },
  { name: "phone", label: "PHONE", type: "tel", testid: "checkout-phone", span: false },
  { name: "pin", label: "PIN CODE", type: "text", testid: "checkout-pin", span: false },
  { name: "email", label: "EMAIL", type: "email", testid: "checkout-email", span: true },
  { name: "city", label: "CITY", type: "text", testid: "checkout-city", span: true },
  { name: "address", label: "ADDRESS", type: "text", testid: "checkout-address", span: true },
];

const STEPS = ["ORDER", "DONE"];
const API = process.env.REACT_APP_BACKEND_URL;
const SAVED_KEY = "shutup-buyer";

const loadRazorpay = () =>
  new Promise((resolve) => {
    if (window.Razorpay) return resolve(true);
    const s = document.createElement("script");
    s.src = "https://checkout.razorpay.com/v1/checkout.js";
    s.onload = () => resolve(true);
    s.onerror = () => resolve(false);
    document.body.appendChild(s);
  });

export default function CheckoutModal() {
  const { items, total, checkoutOpen, setCheckoutOpen, clear, rebelLine } = useCart();
  const { user } = useAuth();
  const [done, setDone] = useState(null);
  const [paying, setPaying] = useState(false);
  const [error, setError] = useState(null);
  const [saved] = useState(() => {
    try {
      return JSON.parse(localStorage.getItem(SAVED_KEY)) || {};
    } catch {
      return {};
    }
  });
  const [pin, setPin] = useState(saved?.pin || "");
  const [city, setCity] = useState(saved?.city || "");
  const [pinHint, setPinHint] = useState("");
  const lastPin = useRef("");

  useEffect(() => {
    if (checkoutOpen && items.length) {
      track("begin_checkout", {
        currency: "INR",
        value: total,
        items: items.map((i) => ({
          item_id: PRODUCT.id,
          item_name: PRODUCT.name,
          item_variant: `${i.color || "BLACK"} / ${i.size}`,
          price: PRODUCT.price,
          quantity: i.qty,
        })),
      });
    }
  }, [checkoutOpen]);

  const lookupPin = async (value) => {
    setPin(value);
    if (!/^\d{6}$/.test(value) || value === lastPin.current) return;
    lastPin.current = value;
    try {
      const res = await fetch(`https://api.postalpincode.in/pincode/${value}`);
      const data = await res.json();
      const po = data?.[0]?.Status === "Success" ? data[0].PostOffice?.[0] : null;
      const place = po?.District || po?.Block || "";
      if (place) {
        setCity(place);
        setPinHint(`CITY SPOTTED → ${place.toUpperCase()}${po.State ? `, ${po.State.toUpperCase()}` : ""}`);
      } else {
        setPinHint("");
      }
    } catch {
      setPinHint("");
    }
  };

  const close = () => {
    setCheckoutOpen(false);
    if (done) setDone(null);
    setError(null);
  };

  const pay = async (e) => {
    e.preventDefault();
    const customer = Object.fromEntries(new FormData(e.target));
    setPaying(true);
    setError(null);
    const snapshot = { items, total, rebelLine };
    try {
      const loaded = await loadRazorpay();
      if (!loaded) throw new Error("sdk");
      let orderData = null;
      for (let attempt = 0; attempt < 2 && !orderData; attempt++) {
        if (attempt) await new Promise((r) => setTimeout(r, 2500));
        try {
          const res = await fetch(`${API}/api/razorpay/order`, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ items, customer, rebel_line: rebelLine }),
          });
          if (res.ok) orderData = await res.json();
        } catch {
          /* network blip — loop retries once */
        }
      }
      if (!orderData) throw new Error("order");
      localStorage.setItem(SAVED_KEY, JSON.stringify(customer));

      const rzp = new window.Razorpay({
        key: orderData.key_id,
        amount: orderData.amount,
        currency: "INR",
        order_id: orderData.order_id,
        name: "THE SHUTUP COMPANY",
        description: "THE SHUTUP T-SHIRT",
        theme: { color: "#FF10F0" },
        method: { upi: true, card: true, netbanking: true, wallet: true },
        prefill: { name: customer.name, email: customer.email, contact: customer.phone },
        handler: async (resp) => {
          try {
            const v = await fetch(`${API}/api/razorpay/verify`, {
              method: "POST",
              headers: { "Content-Type": "application/json" },
              body: JSON.stringify({
                razorpay_order_id: resp.razorpay_order_id,
                razorpay_payment_id: resp.razorpay_payment_id,
                razorpay_signature: resp.razorpay_signature,
              }),
            });
            if (!v.ok) throw new Error("verify");
            const vd = await v.json();
            setDone({ id: vd.payment_id, ...snapshot });
            track("purchase", {
              transaction_id: vd.payment_id,
              currency: "INR",
              value: snapshot.total,
              items: snapshot.items.map((i) => ({
                item_id: PRODUCT.id,
                item_name: PRODUCT.name,
                item_variant: `${i.color || "BLACK"} / ${i.size}`,
                price: PRODUCT.price,
                quantity: i.qty,
              })),
            });
            clear();
            setPaying(false);
          } catch {
            setError("payment went through but verification hiccuped — message us via CONTACT.");
            setPaying(false);
          }
        },
        modal: {
          ondismiss: () => {
            setPaying(false);
            setError("payment cancelled — no money moved. hit pay again whenever you're ready.");
          },
        },
      });
      rzp.on("payment.failed", () => {
        setPaying(false);
        setError("payment failed — no money moved. try again?");
      });
      rzp.open();
    } catch (err) {
      setError(
        err?.message === "sdk"
          ? "payment system didn't load — check your internet and try again."
          : "couldn't reach our server to start payment — try again in a moment. if it persists, message us via CONTACT."
      );
      setPaying(false);
    }
  };

  return (
    <AnimatePresence>
      {checkoutOpen && (
        <>
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={close}
            className="fixed inset-0 z-[80] bg-black/80 backdrop-blur-sm"
          />
          <motion.div
            data-testid="checkout-modal"
            data-lenis-prevent
            initial={{ opacity: 0, y: 60, scale: 0.97 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 60, scale: 0.97 }}
            transition={{ duration: 0.45, ease: [0.16, 1, 0.3, 1] }}
            className="fixed inset-x-3 top-[4vh] bottom-[4vh] sm:inset-x-0 sm:top-[6vh] sm:bottom-[6vh] sm:mx-auto z-[90] sm:max-w-lg bg-paper text-ink border-2 border-black shadow-[10px_10px_0_#FF10F0] overflow-y-auto"
          >
            <div className="flex items-center justify-between px-6 py-5 border-b-2 border-black sticky top-0 bg-paper z-10">
              <p className="font-display text-xl tracking-wide">CHECKOUT</p>
              <button
                data-testid="checkout-close-button"
                onClick={close}
                className="font-sans font-bold text-sm tracking-[0.2em] hover:text-pink transition-colors"
              >
                CLOSE ✕
              </button>
            </div>

            <div className="hidden sm:flex gap-2 px-6 pt-5">
              {STEPS.map((s, i) => (
                <span
                  key={s}
                  className={`font-sans text-[10px] font-bold tracking-[0.25em] px-3 py-1.5 border ${
                    (i === 1) === Boolean(done) ? "bg-black text-paper border-black" : "text-black/40 border-black/20"
                  }`}
                >
                  0{i + 1} {s}
                </span>
              ))}
            </div>

            <div className="px-4 py-4 sm:px-6 sm:py-6">
              {!done && (
                <form onSubmit={pay} data-testid="checkout-details-form">
                  <div className="border-2 border-black p-4 sm:p-5 mb-4">
                    <div className="flex justify-between font-sans text-sm">
                      <span className="text-black/60">
                        {PRODUCT.name} × {items.reduce((a, i) => a + i.qty, 0)}
                      </span>
                      <span className="font-bold">{inr(total)}</span>
                    </div>
                    <div className="flex justify-between font-sans font-bold text-lg mt-3 pt-3 border-t border-black/20">
                      <span>TOTAL</span>
                      <span data-testid="checkout-total">{inr(total)}</span>
                    </div>
                    <p className="font-sans text-black/60 text-[11px] font-semibold tracking-[0.18em] mt-3" data-testid="checkout-shipping-note">
                      FREE SHIPPING ACROSS INDIA — SHIPS IN 3–5 DAYS
                    </p>
                  </div>

                  <div className="grid grid-cols-2 gap-3 sm:gap-4" data-testid="checkout-payment-step">
                    {FIELDS.map((f) => (
                      <label key={f.name} className={`block ${f.span ? "col-span-2" : ""}`}>
                        <span className="font-sans text-[10px] font-bold tracking-[0.25em] text-black/60">{f.label}</span>
                        {f.name === "pin" || f.name === "city" ? (
                          <input
                            required
                            type={f.type}
                            name={f.name}
                            inputMode={f.name === "pin" ? "numeric" : undefined}
                            value={f.name === "pin" ? pin : city}
                            onChange={
                              f.name === "pin"
                                ? (e) => lookupPin(e.target.value.replace(/\D/g, "").slice(0, 6))
                                : (e) => setCity(e.target.value)
                            }
                            data-testid={f.testid}
                            className="mt-1 w-full border-2 border-black bg-transparent px-4 py-2.5 sm:py-3 font-sans text-sm outline-none focus:bg-acid/30"
                          />
                        ) : (
                          <input
                            required
                            type={f.type}
                            name={f.name}
                            defaultValue={
                              saved?.[f.name] ||
                              (f.name === "name" ? user?.name : f.name === "email" ? user?.email : "") ||
                              ""
                            }
                            data-testid={f.testid}
                            className="mt-1 w-full border-2 border-black bg-transparent px-4 py-2.5 sm:py-3 font-sans text-sm outline-none focus:bg-acid/30"
                          />
                        )}
                        {f.name === "pin" && pinHint && (
                          <span data-testid="pin-city-hint" className="font-sans text-[10px] font-bold tracking-[0.2em] text-pink mt-1 block">
                            {pinHint}
                          </span>
                        )}
                      </label>
                    ))}
                  </div>

                  <p className="font-hand text-xl sm:text-2xl text-black/60 -rotate-1 mt-4 mb-3">
                    secure payments by razorpay — your money's safe.
                  </p>
                  {error && (
                    <p className="font-sans text-blood text-xs font-bold tracking-[0.15em] mb-4" data-testid="checkout-error">
                      {error}
                    </p>
                  )}
                  <button
                    data-testid="pay-button"
                    type="submit"
                    disabled={paying}
                    className="w-full bg-black text-paper font-sans font-bold tracking-[0.15em] py-4 hover:bg-pink hover:text-black transition-colors disabled:opacity-60"
                  >
                    {paying ? "OPENING RAZORPAY…" : `PAY ${inr(total)} — UPI · CARDS · NETBANKING`}
                  </button>
                </form>
              )}

              {done && (
                <div className="text-center py-6" data-testid="order-confirmation">
                  <p className="font-display text-5xl sm:text-6xl leading-none">
                    PAID<span className="text-pink">.</span>
                  </p>
                  <p className="font-sans font-bold text-sm tracking-[0.3em] mt-6">ORDER CONFIRMED</p>
                  <p className="font-sans text-xs text-black/50 mt-2 break-all" data-testid="order-id">
                    {done.id}
                  </p>
                  <p className="font-sans text-black/60 text-sm mt-4">
                    {done.items.reduce((a, i) => a + i.qty, 0)} × THE SHUTUP T-SHIRT — {inr(done.total)}
                  </p>
                  {done.rebelLine && (
                    <p className="font-hand text-2xl text-pink mt-4 -rotate-1" data-testid="order-rebel-line">
                      “{done.rebelLine}” — noted. loudly.
                    </p>
                  )}
                  <p className="font-hand text-2xl text-black/60 mt-6 rotate-1">wear it loud.</p>
                  <button
                    data-testid="order-continue-button"
                    onClick={close}
                    className="mt-8 w-full bg-black text-paper font-sans font-bold tracking-[0.15em] py-4 hover:bg-pink hover:text-black transition-colors"
                  >
                    BACK TO THE NOISE →
                  </button>
                </div>
              )}
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
}
